import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaEdit,
  FaTrash,
  FaSearch,
  FaUserCheck,
  FaUserTimes,
  FaKey,
  FaCopy,
} from "react-icons/fa";
import adminService from "../../services/adminService";
import authService from "../../services/authService";
import Footer from "../../components/layout/Footer";
import AdminHeader from "../../components/layout/AdminHeader";

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [modal, setModal] = useState({ isOpen: false, type: "success", message: "" });
  void modal;
  const [editModal, setEditModal] = useState({ isOpen: false, user: null });
  const [editFormData, setEditFormData] = useState({});
  const [confirmModal, setConfirmModal] = useState({ 
    isOpen: false, 
    message: "", 
    onConfirm: null 
  });
  const [credentialsModal, setCredentialsModal] = useState({
    isOpen: false,
    user: null,
    generatedPassword: null
  });
  const [resetPasswordModal, setResetPasswordModal] = useState({
    isOpen: false,
    user: null
  });
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
    captchaInput: ''
  });
  const [captcha, setCaptcha] = useState(() =>
    Math.floor(1000 + Math.random() * 9000)
  );

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await adminService.getUsers();
      if (response.status === "success") {
        setUsers(response.data.results || []);
      }
    } catch (err) {
      setModal({
        isOpen: true,
        type: "error",
        message: "Failed to load users: " + err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    setConfirmModal({
      isOpen: true,
      message: "Are you sure you want to update this user?",
      onConfirm: async () => {
        try {
          await adminService.updateUser(userId, {
            is_active: !currentStatus,
          });
          setConfirmModal({ isOpen: false, message: "", onConfirm: null });
          window.location.reload();
        } catch (err) {
          console.error("Failed to update user status:", err);
          setConfirmModal({ isOpen: false, message: "", onConfirm: null });
          window.location.reload();
        }
      }
    });
  };

  const handleDeleteUser = async (userId, userEmail) => {
    setConfirmModal({
      isOpen: true,
      message: "Are you sure you want to delete this user?",
      onConfirm: async () => {
        try {
          await adminService.deleteUser(userId);
          setConfirmModal({ isOpen: false, message: "", onConfirm: null });
          window.location.reload();
        } catch (err) {
          console.error("Failed to delete user:", err);
          setConfirmModal({ isOpen: false, message: "", onConfirm: null });
          window.location.reload();
        }
      }
    });
  };

  const openEditModal = (user) => {
    setEditFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      organization: user.organization || "",
      job_title: user.job_title || "",
      user_type: user.user_type,
      is_active: user.is_active,
    });
    setEditModal({ isOpen: true, user });
  };

  const closeEditModal = () => {
    setEditModal({ isOpen: false, user: null });
    setEditFormData({});
  };

  const handleEditChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setEditFormData({
      ...editFormData,
      [e.target.name]: value,
    });
  };

  const handleSaveEdit = async () => {
    setConfirmModal({
      isOpen: true,
      message: "Are you sure you want to update this user?",
      onConfirm: async () => {
        try {
          await adminService.updateUser(editModal.user.id, editFormData);
          closeEditModal();
          setConfirmModal({ isOpen: false, message: "", onConfirm: null });
          window.location.reload();
        } catch (err) {
          console.error("Failed to update user:", err);
          closeEditModal();
          setConfirmModal({ isOpen: false, message: "", onConfirm: null });
          window.location.reload();
        }
      }
    });
  };

  const openCredentialsModal = (user) => {
    setCredentialsModal({ isOpen: true, user, generatedPassword: null });
  };

  const closeCredentialsModal = () => {
    setCredentialsModal({ isOpen: false, user: null, generatedPassword: null });
  };

  const generateNewPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCredentialsModal({
      ...credentialsModal,
      generatedPassword: password
    });
  };

  const saveGeneratedPassword = async () => {
    if (!credentialsModal.generatedPassword) return;
    
    try {
      await adminService.updateUser(credentialsModal.user.id, {
        password: credentialsModal.generatedPassword
      });
      
      // Close credentials modal
      closeCredentialsModal();
      
      // Show success notification
      setModal({
        isOpen: true,
        type: "success",
        message: "Password updated successfully!"
      });
      
      // Check if admin is updating their own password
      const currentUser = authService.getCurrentUser();
      const isOwnPassword = currentUser && currentUser.id === credentialsModal.user.id;
      
      if (isOwnPassword) {
        // Logout admin after 2 seconds if they updated their own password
        setTimeout(() => {
          setModal({ isOpen: false, type: "success", message: "" });
          authService.logout();
          navigate('/admin/login');
        }, 2000);
      } else {
        // Just close notification after 2 seconds
        setTimeout(() => {
          setModal({ isOpen: false, type: "success", message: "" });
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to update password:', err);
      setModal({
        isOpen: true,
        type: "error",
        message: "Failed to update password"
      });
      setTimeout(() => {
        setModal({ isOpen: false, type: "error", message: "" });
      }, 2000);
    }
  };

  const openResetPasswordModal = (user) => {
    setResetPasswordModal({ isOpen: true, user });
    setResetPasswordForm({
      newPassword: '',
      confirmPassword: '',
      captchaInput: ''
    });
    setCaptcha(Math.floor(1000 + Math.random() * 9000));
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModal({ isOpen: false, user: null });
    setResetPasswordForm({
      newPassword: '',
      confirmPassword: '',
      captchaInput: ''
    });
  };

  const refreshCaptcha = () => {
    setCaptcha(Math.floor(1000 + Math.random() * 9000));
    setResetPasswordForm({ ...resetPasswordForm, captchaInput: '' });
  };

  const handleResetPasswordChange = (e) => {
    setResetPasswordForm({
      ...resetPasswordForm,
      [e.target.name]: e.target.value
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetPasswordForm.captchaInput !== captcha.toString()) {
      setModal({
        isOpen: true,
        type: "error",
        message: "Invalid captcha. Please try again."
      });
      refreshCaptcha();
      setTimeout(() => {
        setModal({ isOpen: false, type: "error", message: "" });
      }, 2000);
      return;
    }

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      setModal({
        isOpen: true,
        type: "error",
        message: "New password and confirm password do not match."
      });
      setTimeout(() => {
        setModal({ isOpen: false, type: "error", message: "" });
      }, 2000);
      return;
    }
    try {
      await adminService.updateUser(resetPasswordModal.user.id, {
        password: resetPasswordForm.newPassword
      });
      
      // Close reset password modal
      closeResetPasswordModal();
      
      // Show success notification
      setModal({
        isOpen: true,
        type: "success",
        message: "User updated successfully!"
      });
      
      // Check if admin is updating their own password
      const currentUser = authService.getCurrentUser();
      const isOwnPassword = currentUser && currentUser.id === resetPasswordModal.user.id;
      
      if (isOwnPassword) {
        // Logout admin after 2 seconds if they updated their own password
        setTimeout(() => {
          setModal({ isOpen: false, type: "success", message: "" });
          authService.logout();
          navigate('/admin/login');
        }, 2000);
      } else {
        // Just close notification after 2 seconds
        setTimeout(() => {
          setModal({ isOpen: false, type: "success", message: "" });
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to reset password:', err);
      setModal({
        isOpen: true,
        type: "error",
        message: "Failed to reset password: " + (err.message || 'Unknown error')
      });
      setTimeout(() => {
        setModal({ isOpen: false, type: "error", message: "" });
      }, 2000);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setModal({
        isOpen: true,
        type: "success",
        message: `${label} copied to clipboard!`
      });
      setTimeout(() => {
        setModal({ isOpen: false, type: "success", message: "" });
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.organization?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === ""
        ? true
        : filterStatus === "active"
        ? user.is_active
        : !user.is_active;

    return matchesSearch && matchesStatus;
  });

  const pageClass = "min-h-screen bg-[#111318] text-slate-200";
  const panelClass =
    "rounded-xl border border-slate-700/50 bg-slate-800/50 p-4 shadow-xl backdrop-blur-sm sm:p-6";
  const inputClass =
    "w-full rounded-md border border-slate-700/30 bg-slate-950/60 px-3 py-2.5 text-sm text-slate-200 outline-none transition focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60";
  const modalOverlayClass =
    "fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4";
  const confirmOverlayClass =
    "fixed inset-0 z-[2000] flex items-center justify-center bg-black/75 p-4";
  const modalCardClass =
    "w-full max-w-[600px] max-h-[80vh] overflow-y-auto rounded-xl border border-slate-700/40 bg-slate-800/95 p-5 shadow-xl sm:p-8";
  const confirmCardClass =
    "w-full max-w-[450px] rounded-xl border border-slate-700/30 bg-[#111318]/98 p-6 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.5)] sm:p-8";
  const credentialsCardClass =
    "w-full max-w-[550px] rounded-xl border border-slate-700/30 bg-[#111318]/98 p-6 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.5)] sm:p-8";
  const modalTitleClass = "mb-6 text-2xl font-semibold text-slate-50";
  const labelClass = "text-sm font-medium text-slate-300";
  const secondaryButtonClass =
    "rounded-md border border-slate-600/30 bg-slate-500/10 px-4 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:border-slate-500/50 hover:text-slate-200";
  const primaryButtonClass =
    "rounded-md bg-blue-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-400";
  const dangerButtonClass =
    "rounded-md border border-red-500/30 bg-red-500/10 px-5 py-3 text-sm font-semibold text-red-400 transition-colors hover:bg-red-500/15";
  const iconButtonClass =
    "inline-flex h-9 w-9 items-center justify-center rounded-md border border-blue-500/20 bg-blue-500/10 transition-colors hover:bg-blue-500/15";
  const tableHeaderCellClass =
    "border-b border-slate-700/30 px-3 py-3 text-left text-sm font-semibold whitespace-nowrap text-slate-400";
  const tableCellClass = "px-3 py-3 text-sm align-top text-slate-200";

  return (
    <>
    <div className={pageClass}>

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className={confirmOverlayClass}>
          <div className={confirmCardClass}>
            <h2 className="mb-4 text-center text-xl font-semibold text-slate-50">
              Confirm Action
            </h2>
            <p className="mb-6 text-center text-base leading-7 text-slate-400">
              {confirmModal.message}
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                onClick={() => {
                  setConfirmModal({ isOpen: false, message: "", onConfirm: null });
                }}
                className={dangerButtonClass}
              >
                No
              </button>
              <button
                onClick={() => {
                  if (confirmModal.onConfirm) {
                    confirmModal.onConfirm();
                  }
                }}
                className={`${primaryButtonClass} px-5 py-3 font-semibold`}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {credentialsModal.isOpen && credentialsModal.user && (
        <div className={confirmOverlayClass}>
          <div className={credentialsCardClass}>
            <h2 className="mb-6 text-center text-xl font-semibold text-slate-50">
              User Credentials
            </h2>
            <div className="mb-6">
              <div className="mb-5">
                <label className="mb-2 block text-sm font-medium text-slate-400">
                  Login ID (Email)
                </label>
                <div className="flex flex-col gap-3 rounded-md border border-slate-700/30 bg-slate-800/50 p-3 sm:flex-row sm:items-center">
                  <span className="flex-1 break-all font-mono text-sm text-slate-200 sm:text-[15px]">
                    {credentialsModal.user.email}
                  </span>
                  <button
                    onClick={() => copyToClipboard(credentialsModal.user.email, 'Email')}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:bg-blue-500/15"
                    title="Copy to clipboard"
                  >
                    <FaCopy /> Copy
                  </button>
                </div>
              </div>
              <div className="mb-5">
                <label className="mb-2 block text-sm font-medium text-slate-400">
                  Password
                </label>
                {credentialsModal.generatedPassword ? (
                  <div className="flex flex-col gap-3 rounded-md border border-slate-700/30 bg-slate-800/50 p-3 sm:flex-row sm:items-center">
                    <span className="flex-1 break-all font-mono text-sm text-slate-200 sm:text-[15px]">
                      {credentialsModal.generatedPassword}
                    </span>
                    <button
                      onClick={() => copyToClipboard(credentialsModal.generatedPassword, 'Password')}
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:bg-blue-500/15"
                      title="Copy to clipboard"
                    >
                      <FaCopy /> Copy
                    </button>
                  </div>
                ) : (
                  <div className="rounded-md border border-slate-700/30 bg-slate-800/50 p-3">
                    <span className="text-xs italic text-slate-500">
                      Password is encrypted. Click "Generate New Password" to create and view a new password.
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-5 flex flex-col items-stretch gap-3">
              {!credentialsModal.generatedPassword ? (
                <button
                  onClick={generateNewPassword}
                  className="w-full rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-400"
                >
                  Generate New Password
                </button>
              ) : (
                <button
                  onClick={saveGeneratedPassword}
                  className={primaryButtonClass}
                >
                  Save New Password
                </button>
              )}
              <button
                onClick={() => openResetPasswordModal(credentialsModal.user)}
                className="w-full rounded-md bg-amber-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-amber-400"
              >
                Reset Password
              </button>
              <button
                onClick={closeCredentialsModal}
                className={secondaryButtonClass}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal.isOpen && resetPasswordModal.user && (
        <div className={confirmOverlayClass}>
          <div className={credentialsCardClass}>
            <h2 className="mb-6 text-center text-xl font-semibold text-slate-50">
              Reset Password for {resetPasswordModal.user.email}
            </h2>
            <form onSubmit={handleResetPassword} className="mb-6">
              <div className="mb-5">
                <label className="mb-2 block text-sm font-medium text-slate-400">
                  New Password *
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={resetPasswordForm.newPassword}
                  onChange={handleResetPasswordChange}
                  className={inputClass}
                  required
                  placeholder="Enter new password"
                  minLength="6"
                />
              </div>
              <div className="mb-5">
                <label className="mb-2 block text-sm font-medium text-slate-400">
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={resetPasswordForm.confirmPassword}
                  onChange={handleResetPasswordChange}
                  className={inputClass}
                  required
                  placeholder="Confirm new password"
                />
              </div>
              <div className="mb-5">
                <label className="mb-2 block text-sm font-medium text-slate-400">
                  Captcha *
                </label>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <input
                    type="text"
                    name="captchaInput"
                    value={resetPasswordForm.captchaInput}
                    onChange={handleResetPasswordChange}
                    className={`${inputClass} min-w-0 flex-1`}
                    required
                    placeholder="Enter captcha"
                  />
                  <div className="flex min-w-[120px] items-center justify-center gap-2 rounded-md border-2 border-slate-200 bg-slate-100 px-4 py-3">
                    <span className="select-none text-lg font-bold tracking-[0.2em] text-slate-800">
                      {captcha}
                    </span>
                    <button
                      type="button"
                      className="p-0 text-xl leading-none text-blue-600 transition-transform hover:rotate-180"
                      onClick={refreshCaptcha}
                      title="Refresh Captcha"
                    >
                      ↻
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={closeResetPasswordModal}
                  className={dangerButtonClass}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${primaryButtonClass} px-5 py-3 font-semibold`}
                >
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal.isOpen && (
        <div className={modalOverlayClass} onClick={closeEditModal}>
          <div className={modalCardClass} onClick={(e) => e.stopPropagation()}>
            <h2 className={modalTitleClass}>Edit User</h2>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className={labelClass}>First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={editFormData.first_name}
                  onChange={handleEditChange}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={editFormData.last_name}
                  onChange={handleEditChange}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditChange}
                  className={inputClass}
                  disabled
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Organization</label>
                <input
                  type="text"
                  name="organization"
                  value={editFormData.organization}
                  onChange={handleEditChange}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Job Title</label>
                <input
                  type="text"
                  name="job_title"
                  value={editFormData.job_title}
                  onChange={handleEditChange}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>User Type</label>
                <select
                  name="user_type"
                  value={editFormData.user_type}
                  onChange={handleEditChange}
                  className={inputClass}
                >
                  <option value="researcher">Researcher</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="flex cursor-pointer items-center text-sm text-slate-300">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={editFormData.is_active}
                    onChange={handleEditChange}
                    className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500/20"
                  />
                  <span className="ml-2">Active User</span>
                </label>
              </div>
              <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button onClick={closeEditModal} className={secondaryButtonClass}>
                  Cancel
                </button>
                <button onClick={handleSaveEdit} className={primaryButtonClass}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminHeader
        title="User Management"
        subtitle="Manage all users and admins"
      />

      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
        {/* Search and Filter */}
        <div className={`${panelClass} mb-6 flex flex-col gap-4 lg:flex-row lg:items-center`}>
          <div className="flex flex-1 items-center gap-3 rounded-lg border border-slate-700/30 bg-slate-950/60 px-4 py-3">
            <FaSearch className="shrink-0 text-slate-500" />
            <input
              type="text"
              placeholder="Search users by name, email, or organization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent text-sm text-slate-200 outline-none placeholder:text-slate-500"
            />
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={`${inputClass} min-w-[180px]`}
            >
              <option value="">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className={panelClass}>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-50">
              All Users ({filteredUsers.length})
            </h3>
          </div>

          {loading ? (
            <div className="py-16 text-center text-base text-slate-500">
              Loading users...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] border-collapse">
                <thead>
                  <tr>
                    <th className={tableHeaderCellClass}>Name</th>
                    <th className={tableHeaderCellClass}>Email</th>
                    <th className={tableHeaderCellClass}>Organization</th>
                    <th className={tableHeaderCellClass}>Job Title</th>
                    <th className={tableHeaderCellClass}>Type</th>
                    <th className={tableHeaderCellClass}>Status</th>
                    <th className={tableHeaderCellClass}>Joined</th>
                    <th className={tableHeaderCellClass}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-slate-700/20 last:border-b-0"
                    >
                      <td className={tableCellClass}>
                        {user.first_name} {user.last_name}
                      </td>
                      <td className={`${tableCellClass} break-all`}>{user.email}</td>
                      <td className={tableCellClass}>{user.organization || "N/A"}</td>
                      <td className={tableCellClass}>{user.job_title || "N/A"}</td>
                      <td className={tableCellClass}>
                        <span
                          className={`inline-block rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                            user.user_type === "admin"
                              ? "bg-violet-500/10 text-violet-400"
                              : "bg-blue-500/10 text-blue-400"
                          }`}
                        >
                          {user.user_type}
                        </span>
                      </td>
                      <td className={tableCellClass}>
                        <span
                          className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                            user.is_active
                              ? "bg-emerald-500/10 text-emerald-400"
                              : "bg-red-500/10 text-red-400"
                          }`}
                        >
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className={tableCellClass}>
                        {new Date(user.date_joined).toLocaleDateString()}
                      </td>
                      <td className={tableCellClass}>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className={iconButtonClass}
                            title="Edit User"
                          >
                            <FaEdit color="#3b82f6" />
                          </button>
                          <button
                            onClick={() =>
                              handleToggleStatus(user.id, user.is_active)
                            }
                            className={iconButtonClass}
                            title={user.is_active ? "Deactivate" : "Activate"}
                          >
                            {user.is_active ? (
                              <FaUserTimes color="#ef4444" />
                            ) : (
                              <FaUserCheck color="#10b981" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            className={iconButtonClass}
                            title="Delete User"
                          >
                            <FaTrash color="#ef4444" />
                          </button>
                          <button
                            onClick={() => openCredentialsModal(user)}
                            className={iconButtonClass}
                            title="Show Credentials"
                          >
                            <FaKey color="#a855f7" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && (
                <div className="py-16 text-center text-base text-slate-500">
                  No users found
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    <Footer />
    </>
  );
};

export default AdminUserManagement;
